/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { Waiting } from '@msdyn365-commerce-modules/utilities';

import { ISearchResultModalViewProps } from './components';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISortByViewProps,
    ITitleViewProps
} from './krannich-c-search-result-container';
import InfiniteScroll from 'react-infinite-scroller';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps> = props => {
    const {
        SearchResultContainer,
        products,
        ProductsContainer,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        errorMessage,
        FeatureSearchContainer,
        similarLookProduct,
        renderGridAndListView,
        loading,
        data: {
            listPageState: { result: listPageState }
        },
        categoryDiscription,
        resources
    } = props;

    const krannichActiveProducts = [...new Set(listPageState?.activeProducts)];
    const [selectedFilterCount, setSelectedFilterCount] = React.useState<number | undefined>();
    const [isSelectedFilterCount, setIsSelectedFilterCount] = React.useState<boolean>(false);
    React.useEffect(() => {
        if (
            props.data?.listPageState?.result?.activeFilters?.length! > 0 &&
            props.data?.listPageState?.result?.activeFilters?.length! !== undefined
        ) {
            setTimeout(() => {
                setSelectedFilterCount(krannichActiveProducts.length ? props.data.listPageState.result?.totalProductCount : 0);
                setIsSelectedFilterCount(true);
            }, 100);
        } else {
            setIsSelectedFilterCount(false);
        }
    }, [
        props.data.listPageState.result?.activeFilters,
        props.data.listPageState.result?.totalProductCount,
        props.data.listPageState.result?.activeProducts
    ]);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {renderCategoryHierarchy(categoryHierarchy)}
                {renderTitle(TitleViewProps)}
                {categoryDiscription}
                {choiceSummary}
                {modalToggle}
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage, resources.filtersLabel)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {loading ? (
                    <Waiting />
                ) : (
                    <InfiniteScroll threshold={100} pageStart={0} loadMore={props.loadMore} hasMore={true || false}>
                        <Node {...ProductsContainer}>
                            {errorMessage}
                            {products}
                        </Node>
                    </InfiniteScroll>
                )}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            <Node {...CategoryNavContainer}>{categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}</Node>
            <div className='ms-search-result-container'>
                <div className='ms-search-result-container__title-count'>
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && isSelectedFilterCount && !loading
                        ? _renderTitleCount(TitleViewProps, selectedFilterCount, resources.productsLabel)
                        : null}
                </div>
                {categoryDiscription}
            </div>
            <Node {...RefineAndProductSectionContainer}>
                {refineMenu && renderRefiner(refineMenu, resources.filtersLabel)}
                <Node {...ProductSectionContainer}>
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {choiceSummary}
                    {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions, renderGridAndListView)}
                    <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                    {loading ? (
                        <Waiting />
                    ) : (
                        <InfiniteScroll threshold={100} pageStart={0} loadMore={props.loadMore} hasMore={true || false}>
                            <Node {...ProductsContainer}>
                                {errorMessage}
                                {products}
                            </Node>
                        </InfiniteScroll>
                    )}
                </Node>
            </Node>
        </Module>
    );
};

const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    filtersLabel?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage, filtersLabel),
        modalProps.modalFooter
    );
};

const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    filtersLabel?: string
): JSX.Element | null => {
    return React.cloneElement(
        props.modalBody,
        {},
        sortByDropDown && renderSort(sortByDropDown, isRecoSearchPage),
        renderRefiner(refineMenu, filtersLabel)
    );
};

const renderRefiner = (props: IRefineMenuViewProps, filtersLabel?: string): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer, activeFilters, activeProducts } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {(activeFilters! > 0 || activeProducts! > 0) && (
                        <div className='ms-search-result-container__refine-menu-filter-title'>{filtersLabel}</div>
                    )}
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, renderGridAndListView: React.ReactNode, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return (
            <Node {...SortingContainer}>
                <div className='ms-search-result-container__sort-view'>
                    {sortByDropDown}
                    {renderGridAndListView}
                </div>
            </Node>
        );
    }
    return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h2>
                    {title.titlePrefix}
                    {title.titleText}
                </h2>
            </Node>
        );
    }
    return null;
};

const _renderTitleCount = (props: ITitleViewProps, count?: number, productsLabel?: string): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title && count) {
        return (
            <Node {...TitleContainer}>
                <h5>
                    <span className='ms-search-result__collection-title-count'>
                        {count} {productsLabel}
                    </span>
                </h5>
            </Node>
        );
    }
    return null;
};
export default SearchResultContainerView;
